<template>
  <div>
    <TimeLineChart :workLogs="currentWorklogs"
                   :startTime="toSelectedDate(data[selectedDate] ? data[selectedDate].startTime : 0)"
                   @drag="updateDrag" @click="addLogAt" :topicsMap="topicsMap" @splitWorkLog="splitWorkLog"/>
    <div class="ui basic segment">
      <div class="custom-grid">
        <div class="column">
          <table class="ui compact celled table">
            <thead>
            <tr>
              <th colspan="2">Thema</th>
              <th>Von</th>
              <th>Bis</th>
              <th>Dauer</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <template v-for="(log, i) in currentWorklogs">
              <tr v-bind:key="i" :class="{positive: log.isDone}">
                <td class="collapsing" :style="{'background-color': topicsMap[log.topic].color}"></td>
                <td>
                  <TopicInput :class="{icon: log.isBreak, warning: log.isBreak}" v-model="log.topic"
                              @topicChange="() => {topicChange(log);}" :tags="getTags(log)" :isBreak="log.isBreak"
                              :suggestions="latestTopics"></TopicInput>
                </td>
                <td class="collapsing">
                  <TimeInput :time="toSelectedDate(log.from)"
                             @input="(value) => { if(value < log.to) { $set(log, 'from', value); }}"/>
                </td>
                <td class="collapsing">
                  <TimeInput :time="toSelectedDate(log.to)"
                             @input="(value) => { if(value > log.from) { $set(log, 'to', value); }}"/>
                </td>
                <td class="collapsing">{{ durationToJira(log.to - log.from) }}</td>
                <td class="collapsing">
                  <button class="ui tiny inverted red icon button" @click="deleteWorkLog(i)" title="Löschen">
                    <i class="ui remove icon"></i>
                  </button>
                  <CommentInput :comment="log.comment ? log.comment : ''"
                                @input="(value) => {$set(log, 'comment', value);}"></CommentInput>
                  <TagsSelect :options="latestTags" :selected-options="getTags(log) ? getTags(log) : []"
                              @select="(items) => {addTag(log, items);}"></TagsSelect>
                  <button class="ui tiny inverted green icon button" @click="addLog(log)"
                          title="Vom letzten Eintrag bis jetzt auffüllen">
                    <i class="ui step forward icon"></i>
                  </button>
                  <button class="ui tiny inverted blue icon button" @click="showTimePicker(log)"
                          title="Bis zu beliebiger Zeit auffüllen">
                    <i class="ui step clock icon"></i>
                  </button>
                </td>
              </tr>
            </template>
            <DatetimePicker v-if="timePickerShown" :callback="hideTimePicker()" :value="currentTime"
                            :is-time-only="true"></DatetimePicker>
            </tbody>
            <tfoot>
            <tr>
              <th colspan="2">
                <span>Arbeitsbeginn: </span>
                <TimeInput v-if="data[selectedDate]"
                           :time="toSelectedDate($store.getters.fixedStartOfWork && !data[selectedDate].fixedStartOfWorkOverwrite ? $store.getters.startOfWorkTime : data[selectedDate].startTime)"
                           @input="(value) => { setStartOfWork(value); }"/>
              </th>
              <th class="collapsing">
              </th>
              <th>
                <button class="ui tiny inverted orange fluid labeled icon button"
                        @click="addLog({topic: 'PAUSE', isBreak: true});">
                  <i class="ui pause icon"></i>
                  Pause anlegen
                </button>
              </th>
              <th class="collapsing" colspan="2">
                <button class="ui tiny inverted green fluid labeled icon button" @click="addLog">
                  <i class="ui plus icon"></i>
                  Neues Thema
                </button>
              </th>
            </tr>
            </tfoot>
          </table>
        </div>
        <div class="column">
          <table class="ui compact celled table">
            <thead>
            <tr>
              <th colspan="2">Thema</th>
              <th colspan="2">Summierte Zeit</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(topicData, i) in summedTimes" :key="i">
              <td style="word-break: break-all;">{{ topicData.name }}</td>
              <td class="collapsing" style="border-left: none;">
                <i v-if="topicData.comment" class="grey comment icon" :title="topicData.comment"></i>
              </td>
              <td style="width: 200px;">{{ durationToJira(topicData.duration) }}</td>
              <td class="collapsing center aligned">
                <template v-if="topicData.isBreak != true">
                  <a v-if="getJiraLink(topicData) != null" class="ui tiny basic icon button"
                     :href="getJiraLink(topicData)" target="_blank" title="Öffne Ticket in Jira"
                     @click.middle="toggleDone(topicData, true)" @click="toggleDone(topicData, true)">
                    <i class="icons" style="width: 1.18em;">
                      <i class="icon" style="font-weight: bold">J<i class="ui tiny arrow location icon"
                                                                    style="margin-left: 2px;"></i></i>
                    </i>
                  </a>
                  <button v-else class="ui tiny basic icon button"
                          v-clipboard:copy="durationToJira(topicData.duration)"
                          title="Kopiere 'Summierte Zeit'">
                    <i class="ui copy icon"></i>
                  </button>
                  <button class="ui tiny basic icon button"
                          :class="{green: topicData.count == topicData.doneCount, yellow: topicData.count != topicData.doneCount && topicData.doneCount > 0}"
                          title="Als eingetragen makieren" @click="toggleDone(topicData)">
                    <i class="ui check icon"></i>
                  </button>
                </template>
                <i v-else class="ui disabled pause icon"></i>
              </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
              <th colspan="2">Summiert (mit Pause)</th>
              <th>{{ durationToJira(summedTimesTotal - summedTimeBreak) }} ({{ durationToJira(summedTimesTotal) }})</th>
              <th></th>
            </tr>
            </tfoot>
          </table>
          <table class="ui compact celled table">
            <thead>
            <tr>
              <th>Tag</th>
              <th>Summierte Zeit</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(duration, tag) in summedTimesByTag" :key="tag">
              <td style="word-break: break-all;"><span class="ui horizontal basic label">{{ tag }}</span></td>
              <td style="width: 200px;">{{ durationToJira(duration) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="ui fluid form" v-if="data && data[selectedDate]" style="padding-top: 1em">
                 <textarea name="" id="" cols="30" rows="10" placeholder="Notizen"
                           v-model="data.notes"></textarea>
      </div>
    </div>
  </div>
</template>

<script>

import TimeLineChart from './TimeLineChart.vue'
import TimeInput from './TimeInput.vue'
import CommentInput from './CommentInput.vue'
import {EventBus} from '../eventBus.js';
import TagsSelect from './TagsSelect.vue'
import TopicInput from './TopicInput.vue'
import DatetimePicker from './timepicker/components/VueMaterialDateTimePicker'

const moment = require('moment');

export default {
  name: 'WorkLogs',
  props: ['date'],
  components: {
    TimeLineChart,
    TimeInput,
    CommentInput,
    TagsSelect,
    TopicInput,
    DatetimePicker
  },
  data: function () {
    return {
      selectedDate: '',
      data: {}, // {notes: "", date: [workLogs: {topic, from, to, isBreak}, startTime: timestamp], tags: []}
      timePickerShown: false,
      currentTime: new Date(),
      timePickerLog: null
    }
  },
  watch: {
    data: {
      handler() {
        localStorage.setItem('data', JSON.stringify(this.data));
      },
      deep: true,
    }
  },
  created: function () {
    this.loadData()
    EventBus.$on('backupLoaded', this.loadData)
  },
  beforeDestroy () {
    EventBus.$off('backupLoaded', this.loadData)
  },
  computed: {
    topicsMap: function () {
      const colors = ['#7cb5ec', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'];
      const breakColor = '#ebebeb';

      let map = {};
      let orderIndex = 0;
      let colorIndex = 0;

      if (this.currentWorklogs) {
        for (let log of this.currentWorklogs) {
          if (map[log.topic] == null) {
            map[log.topic] = {
              color: null,
              index: orderIndex++,
              name: log.topic
            }
            if (log.isBreak) {
              map[log.topic].color = breakColor;
            } else {
              map[log.topic].color = colors[colorIndex++ % colors.length];
            }
          }
        }
      }

      return map;
    },
    currentWorklogs: function () {
      if (this.data != null && this.selectedDate != null && this.data[this.selectedDate] != null) {
        //dont sort, to avoid false index refferences
        return this.data[this.selectedDate].workLogs;
      }
      return null;
    },
    summedTimes: function () {
      let data = [];

      if (this.topicsMap && this.currentWorklogs) {

        for (let log of this.currentWorklogs) {
          let topicData = this.logToTopicData(log);

          let foundEntry = null;
          for (let dataEntry of data) {
            if (this.isSameWorklogGroup(topicData, dataEntry)) {
              foundEntry = dataEntry;
              break;
            }
          }

          if (foundEntry == null) {
            data.push(topicData);
          } else {
            foundEntry.duration += topicData.duration;
            foundEntry.count += topicData.count;
            foundEntry.doneCount += topicData.doneCount;
          }
        }
      }

      return data;
    },
    summedTimesByTag: function () {
      let tagDurationMap = {};

      if (this.currentWorklogs) {
        for (let log of this.currentWorklogs) {
          if (log.tags) {
            for (let tagId of log.tags) {
              let tag = this.data.tags[tagId].title;
              if (tagDurationMap[tag] == null) {
                tagDurationMap[tag] = 0;
              }
              tagDurationMap[tag] += log.to - log.from;
            }
          }
        }
      }

      return tagDurationMap;
    },
    summedTimeBreak: function () {
      let sum = 0;

      if (this.currentWorklogs) {
        for (let log of this.currentWorklogs) {
          if (log.isBreak) {
            sum += log.to - log.from;
          }
        }
      }
      return sum;
    },
    summedTimesTotal: function () {
      let sum = 0;

      for (let topicData of Object.values(this.summedTimes)) {
        sum += topicData.duration;
      }
      return sum;
    },
    latestTags: function () {
      let optionMap = {};

      for (let date in this.data) {
        if (typeof this.data[date] === 'object' && this.data[date].workLogs != null) {
          for (let log of this.data[date].workLogs) {
            if (log.tags) {
              for (let i = 0; i < log.tags.length; i++) {
                if (this.data.tags.hasOwnProperty(log.tags[i]) && this.data.tags[log.tags[i]] !== undefined) {
                  const tag = this.data.tags[log.tags[i]].title;
                  optionMap[tag] = true;
                } else {
                  log.tags.splice(i, 1);
                  this.$set(log, 'tags', log.tags);
                }
              }
            }
          }
        }
      }
      const tagIds = Object.keys(this.data.tags);
      for (let i = 0; i < tagIds.length; i++) {
        const tag = this.data.tags[tagIds[i]];
        if (tag.neverUsed) {
          optionMap[tag.title] = true;
        }
      }

      return Object.keys(optionMap);
    },
    latestTopics: function () {
      let optionMap = {};
      let dates = [];

      for (let date in this.data) {
        if (typeof this.data[date] === 'object' && this.data[date].workLogs != null) {
          dates.push(date);
        }
      }

      dates = dates.sort().slice(-1 - this.$store.getters.topicSuggestionHistoryLength);

      for (let date of dates) {
        for (let log of this.data[date].workLogs) {
          if (log.topic) {
            optionMap[log.topic] = true;
          }
        }
      }

      return Object.keys(optionMap);
    }
  },
  methods: {
    loadData () {
      try {
        this.data = JSON.parse(localStorage.getItem('data'));
      } catch (e) {
        console.warn('Could not Parse LocalStorage', e);
      }

      if (this.data == null) {
        this.data = {
          tags: {},
          tagTitleToId: {}
        };
      }

      if (!this.data.hasOwnProperty('tags') && !this.data.hasOwnProperty('tagTitleToId')) {
        this.migrateLocalStorage();
      }
    },
    roundUpToNextStep: function (momentTime) {
      // One step is 5 minutes

      let steps = Math.ceil(momentTime.minute() / 5);
      if (steps == 12) {
        return moment(momentTime).minute(0).second(0).millisecond(0).add(1, 'hour');
      }

      return moment(momentTime).minute(steps * 5).second(0).millisecond(0);
    },
    updateDrag: function (index, log) {
      let dragedLog = this.data[this.selectedDate].workLogs[index];
      dragedLog = Object.assign(dragedLog, log);
      this.$set(this.data[this.selectedDate].workLogs, index, dragedLog);
    },
    showTimePicker: function (log) {
      this.timePickerShown = true
      this.currentTime = new Date()
      this.timePickerLog = log
    },
    hideTimePicker: function () {
      return (date) => {
        this.timePickerShown = false
        if (date !== undefined) {
          let expandLog = this.timePickerLog;

          if (expandLog == null) {
            expandLog = {};
          }

          let now = this.roundUpToNextStep(this.newMoment(date)).valueOf();

          let newLog = {
            from: expandLog.from !== undefined ? expandLog.from : this.data[this.selectedDate].startTime,
            to: now,
            topic: expandLog.topic == null ? '' : expandLog.topic,
            isBreak: expandLog.isBreak,
            comment: expandLog.comment,
            tags: expandLog.tags,
          };

          let successorLog = null;
          let latestLogWithTopic = null;
          let latestLog = null;
          for (let log of this.currentWorklogs) {
            if (expandLog.hasOwnProperty('to') && expandLog.to <= log.from) {
              if (successorLog === null || log.from < successorLog.from) {
                successorLog = log;
              }
            }
            if (this.isSameWorklogGroup(this.logToTopicData(log), this.logToTopicData(newLog)) && (latestLogWithTopic == null || log.to > latestLogWithTopic.to)) {
              latestLogWithTopic = log;
            }
            if (latestLog == null || log.to > latestLog.to) {
              latestLog = log;
            }
          }

          if (successorLog !== null && now <= successorLog.from) {
            this.$set(expandLog, 'to', newLog.to);
            newLog = null;
          } else if (successorLog !== null && now > successorLog.from && now <= successorLog.to) {
            newLog = null;
          } else if (latestLog != null) {
            if (latestLogWithTopic !== latestLog) newLog.from = latestLog.to;

            if (newLog.to <= newLog.from) {
              newLog.to = this.roundUpToNextStep(moment(newLog.from).add(10, 'm')).valueOf();
            }

            if (latestLogWithTopic === latestLog) {
              if (newLog.from < now) {
                this.$set(latestLogWithTopic, 'to', newLog.to);
              }
              newLog = null;
            }
          }

          if (newLog != null) {
            this.data[this.selectedDate].workLogs.push(newLog);
          }
        }
      }
    },
    addLog: function (expandLog, toDate) {

      if (expandLog == null) {
        expandLog = {};
      }

      let to = this.roundUpToNextStep(this.newMoment(toDate)).valueOf();

      let newLog = {
        from: expandLog.from !== undefined ? expandLog.from : (this.$store.getters.fixedStartOfWork ? this.$store.getters.startOfWorkTime : this.data[this.selectedDate].startTime),
        to: to,
        topic: expandLog.topic == null ? '' : expandLog.topic,
        isBreak: expandLog.isBreak,
        comment: expandLog.comment,
        tags: expandLog.tags == null ? [] : [...expandLog.tags],
      };

      let latestLogWithTopic = null;
      let latestLog = null;
      for (let log of this.currentWorklogs) {
        if (this.isSameWorklogGroup(this.logToTopicData(log), this.logToTopicData(newLog)) && (latestLogWithTopic == null || log.to > latestLogWithTopic.to)) {
          latestLogWithTopic = log;
        }
        if (latestLog == null || log.to > latestLog.to) {
          latestLog = log;
        }
      }

      if (latestLog != null) {
        newLog.from = latestLog.to;

        if (newLog.to <= newLog.from) {
          newLog.to = this.roundUpToNextStep(moment(newLog.from).add(10, 'm')).valueOf();
        }

        if (latestLogWithTopic === latestLog) {
          if (newLog.from < to) {
            this.$set(latestLogWithTopic, 'to', newLog.to);
          }
          newLog = null;
        }
      }

      if (newLog != null) {
        this.topicChange(newLog);
        this.data[this.selectedDate].workLogs.push(newLog);
      }
    },
    addLogAt: function (topic, timestamp) {
      let timeslotInUse = false;
      let lowerLimit = this.data[this.selectedDate].startTime;
      let lowerLog = null;
      let upperLimit = this.newMoment(new Date()).valueOf();
      const now = this.newMoment(new Date()).valueOf();
      const futureTimestamp = timestamp > now;
      let latestPastLog = null;
      let latestLog = null;
      let latestLogWithTopic = null;

      this.data[this.selectedDate].workLogs.forEach(log => {
        if (log.to > lowerLimit && log.to <= timestamp) {
          lowerLimit = log.to;
          lowerLog = log;
        }
        if (log.from < upperLimit && log.from >= timestamp) {
          upperLimit = log.from;
        }
        if (log.from <= timestamp && log.to >= timestamp) {
          timeslotInUse = true;
        }
        if ((latestLogWithTopic === null || log.to > latestLogWithTopic.to) && log.topic === topic) {
          latestLogWithTopic = log;
        }
        if (latestLog === null || log.to > latestLog.to) {
          latestLog = log;
        }
        if ((latestPastLog === null || log.to > latestPastLog.to) && log.to <= now) {
          latestPastLog = log;
        }
      });

      lowerLimit = this.roundUpToNextStep(this.newMoment(lowerLimit)).valueOf();
      upperLimit = this.roundUpToNextStep(this.newMoment(upperLimit)).valueOf();

      if (lowerLimit === upperLimit || futureTimestamp) {
        timeslotInUse = true;
      }

      if (!timeslotInUse) {
        let isBreak = this.data[this.selectedDate].workLogs.reduce((isBreak, log) => {
          return isBreak || (log.topic == topic && log.isBreak === true);
        }, false);

        if (lowerLog !== null && latestPastLog !== null && latestPastLog === lowerLog && lowerLog.topic === topic) {
          this.$set(lowerLog, 'to', upperLimit);
        } else {
          const newLog = {
            topic: topic,
            isBreak: isBreak,
            from: lowerLimit,
            to: upperLimit
          };

          this.topicChange(newLog);

          this.data[this.selectedDate].workLogs.push(newLog);
        }
      } else if (futureTimestamp) {
        this.addLog(latestLogWithTopic);
      }
    },
    generateRandomHex: function () {
      return 'xxxxxxxx'.replace(/x/g, () => {
        return (Math.random() * 16 | 0).toString(16);
      });
    },
    generateUniqueRandomHex: function () {
      let randomHex = this.generateRandomHex();
      while (this.data.tags.hasOwnProperty(randomHex)) {
        randomHex = this.generateRandomHex();
      }
      return randomHex;
    },
    addTag: function (log, items) {
      let tagIds = [];
      for (const item of items) {
        if (!(this.data.tagTitleToId.hasOwnProperty(item) && this.data.tagTitleToId[item] !== undefined)) {
          const newTagId = this.generateUniqueRandomHex();
          this.$set(this.data.tags, newTagId, {
            title: item,
            regex: null,
            neverUsed: false
          });
          this.$set(this.data.tagTitleToId, item, newTagId);
        }
        if (this.data.tags[this.data.tagTitleToId[item]].neverUsed) this.$set(this.data.tags[this.data.tagTitleToId[item]], 'neverUsed', false);
        tagIds.push(this.data.tagTitleToId[item]);
      }
      this.$set(log, 'tags', tagIds);
    },
    getTags: function (log) {
      let tags = [];
      if (log !== undefined && log !== null && log.tags !== undefined) {
        for (let i = 0; i < log.tags.length; i++) {
          if (this.data.tags.hasOwnProperty(log.tags[i]) && this.data.tags[log.tags[i]] !== undefined) {
            tags.push(this.data.tags[log.tags[i]].title);
          } else {
            log.tags.splice(i, 1);
            this.$set(log, 'tags', log.tags);
          }
        }
      }
      return tags;
    },
    getDateKeys: function () {
      const dataKeys = Object.keys(this.data);
      const dateRegex = new RegExp('^[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]$');
      let dates = [];
      for (const dataKey of dataKeys) {
        if (dateRegex.test(dataKey)) {
          dates.push(dataKey);
        }
      }
      return dates.sort().reverse();
    },
    topicChange: function (log) {
      if (log.topic == null || !log.topic.trim()) {
        log.tags = undefined;
      }

      dateLoop:
          for (let date of this.getDateKeys()) {
            // put worklogs into new array for reversing
            for (let currentLog of [...this.data[date].workLogs].reverse()) {
              if (this.isSameWorklogGroup(this.logToTopicData(currentLog), this.logToTopicData(log)) && currentLog.to < log.to) {
                if (currentLog.tags) {
                  for (const tag of currentLog.tags) {
                    if (log.tags === undefined || !log.tags.includes(tag)) {
                      if (log.tags === undefined) log.tags = [];
                      log.tags.push(tag);
                    }
                  }
                }
                break dateLoop;
              }
            }
          }
      const tagIds = Object.keys(this.data.tags);
      for (let i = 0; i < tagIds.length; i++) {
        if (this.data.tags[tagIds[i]].regex !== null && (log.tags === undefined || !log.tags.includes(tagIds[i]))) {
          const regex = new RegExp(this.data.tags[tagIds[i]].regex);
          if (regex.test(log.topic)) {
            if (log.tags === undefined) log.tags = [];
            log.tags.push(tagIds[i]);
          }
        }
      }
      this.$set(log, 'tags', log.tags);
    },
    setStartOfWork: function (value) {
      this.data[this.selectedDate].startTime = value;
      if (this.$store.getters.fixedStartOfWork && this.toSelectedDate(this.$store.getters.startOfWorkTime) === this.toSelectedDate(value)) {
        this.data[this.selectedDate].fixedStartOfWorkOverwrite = false;
      } else if (this.$store.getters.fixedStartOfWork) {
        this.data[this.selectedDate].fixedStartOfWorkOverwrite = true;
      }
    },
    selectDate: function (momentTime) {
      momentTime = momentTime.startOf('minute');
      this.selectedDate = momentTime.format('YYYY-MM-DD');
      let now = this.roundUpToNextStep(moment().startOf('minute'));
      if (this.data[this.selectedDate] == null) {
        this.$set(this.data, this.selectedDate, {
          workLogs: [],
          startTime: momentTime.set('hour', now.get('hour')).set('minute', now.get('minute')).valueOf()
        });
      }
    },
    durationToJira: function (timeMs) {
      let durationStrings = [];
      let duration = moment.duration(timeMs);

      if (duration.hours() > 0) {
        durationStrings.push(duration.hours() + 'h');
      }
      if (duration.minutes() > 0) {
        durationStrings.push(duration.minutes() + 'm');
      }
      return durationStrings.join(' ');
    },
    getJiraLink: function (topicData) {
      if (topicData != null && topicData.name != null) {
        let matches = topicData.name.match(/[A-Z0-9]{2,}-[0-9]+/);
        if (matches != null) {
          let jiraUrl = this.$store.getters.jiraUrl;
          if (jiraUrl != null && jiraUrl.trim().length > 0) {
            return jiraUrl + 'CreateWorklog!default.jspa?key=' + encodeURIComponent(matches[0]) +
                '&timeLogged=' + encodeURIComponent(this.durationToJira(topicData.duration)) +
                (topicData.comment ? '&comment=' + encodeURIComponent(topicData.comment) : '');
          }
        }
      }
      return null;
    },
    deleteWorkLog: function (index) {
      let log = this.data[this.selectedDate].workLogs[index];
      if (confirm('Wollen Sie wirklich den folgenden Eintrag löschen?\n' + log.topic + ': ' + moment(log.from).format('HH:mm') + '-' + moment(log.to).format('HH:mm'))) {
        this.data[this.selectedDate].workLogs.splice(index, 1);
      }
    },
    logToTopicData(log) {
      return {
        name: log.topic == null ? '' : log.topic,
        comment: log.comment ? log.comment : '',
        duration: log.to - log.from,
        count: 1,
        doneCount: log.isDone ? 1 : 0,
        isBreak: log.isBreak == true // no null
      };
    },
    isSameWorklogGroup(topicData1, topicData2) {

      if (topicData1.name.trim() == topicData2.name.trim() &&
          topicData1.isBreak == topicData2.isBreak &&
          topicData1.comment.trim() == topicData2.comment.trim()) {
        return true;
      }

      return false;
    },
    toggleDone: function (topicData, newValue) {
      if (newValue != null) {
        newValue = !!newValue;
      } else {
        newValue = false;
        for (let log of this.currentWorklogs) {
          if (this.isSameWorklogGroup(this.logToTopicData(log), topicData) && log.isDone !== true) {
            newValue = true;
            break;
          }
        }
      }
      for (let i = 0; i < this.data[this.selectedDate].workLogs.length; i++) {
        if (this.isSameWorklogGroup(this.logToTopicData(this.data[this.selectedDate].workLogs[i]), topicData)) {
          this.$set(this.data[this.selectedDate].workLogs[i], 'isDone', newValue);
        }
      }
    },
    newMoment: function (date) {
      let now = moment(date);
      let today = moment(this.selectedDate);

      return now.year(today.year())
          .month(today.month())
          .date(today.date())
          .second(0)
          .millisecond(0);
    },
    toSelectedDate: function (timestamp) {
      let time = moment(timestamp);
      return this.newMoment().hour(time.hour()).minute(time.minute()).valueOf();
    },
    migrateLocalStorage: function () {
      this.$set(this.data, 'tags', {});
      this.$set(this.data, 'tagTitleToId', {});
      const dataKeys = Object.keys(this.data);
      const dateRegex = new RegExp('^[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]$');
      let dates = [];
      for (const dataKey of dataKeys) {
        if (dateRegex.test(dataKey)) {
          dates.push(dataKey);
        }
      }
      for (const date of dates) {
        for (const log of this.data[date].workLogs) {
          const tags = [...log.tags];
          this.$set(log, 'tags', []);
          this.addTag(log, tags);
        }
      }
    },
    splitWorkLog: function(workLogIndex) {
      const workLogToSplit = this.currentWorklogs[workLogIndex]
      const halfTime = this.roundUpToNextStep(this.newMoment((workLogToSplit.to + workLogToSplit.from) / 2)).valueOf()
      if(halfTime < workLogToSplit.to) {
        const clonedWorkLog = {
          ...workLogToSplit,
          from: halfTime,
          to: workLogToSplit.to
        }
        workLogToSplit.to = halfTime
        this.data[this.selectedDate].workLogs.splice(workLogIndex + 1, 0, clonedWorkLog);
      }
    }
  },
  mounted: function () {
    let vm = this;
    vm.selectDate(moment(this.date));
    EventBus.$on('selectDate', function (selectedDate) {
      vm.selectDate(moment(selectedDate));
    });

    window.addEventListener('beforeunload', function (e) {
      let hasUncommitedLogs = false;

      if (vm.$store.getters.askNotCommitedLogs && vm.currentWorklogs) {
        for (let log of vm.currentWorklogs) {
          if (!log.isDone && !log.isBreak) {
            hasUncommitedLogs = true;
            break;
          }
        }
      }

      if (hasUncommitedLogs) {
        e.preventDefault();
      }
    });
  }
}
</script>

<style>

.custom-grid > *:last-child {
  padding-top: 1em;
}

@media (min-width: 1500px) {
  .custom-grid {
    display: flex;
  }
  .custom-grid > *:first-child {
    width: 60%;
  }
  .custom-grid > *:last-child {
    padding-left: 1em;
    padding-top: 0;
    width: 40%;
  }
}
</style>
